/**
 * Kotlin bridge generator - Functional implementation with dependency injection
 * Generates Kotlin bridge files for React Native integration
 */

import type {
  ModuleConstant,
  ModuleEvent,
  ModuleMethod,
  ParsedModule,
} from "../../parser";
import type { KotlinCodeEmitter } from "./kotlin-code-emitter";
import type { KotlinMethodGenerator } from "./kotlin-method-generator";

export interface KotlinBridgeGeneratorDeps {
  codeEmitter: KotlinCodeEmitter;
  methodGenerator: KotlinMethodGenerator;
}

export interface KotlinBridgeGenerator {
  /**
   * Generates BrickModuleBase.kt interface
   */
  generateBrickModuleBase(): string;

  /**
   * Generates the main Android bridge file (BrickModule.kt)
   */
  generateAndroidBridge(
    allMethods: Array<ModuleMethod & { moduleName: string }>,
    allConstants: Array<ModuleConstant & { moduleName: string }>,
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string;

  /**
   * Generates Kotlin implementation file (BrickModuleImpl.kt)
   */
  generateKotlinImplementation(
    allMethods: Array<ModuleMethod & { moduleName: string }>,
    allConstants: Array<ModuleConstant & { moduleName: string }>,
    allEvents: Array<ModuleEvent & { moduleName: string }>,
    libraryImports: string
  ): string;

  /**
   * Generates library imports for data types used in BrickModuleImpl
   */
  generateLibraryImports(modules: ParsedModule[]): string;

  /**
   * Generate build.gradle
   */
  generateBuildGradle(
    brickModules: { npmName: string; gradleProjectName: string }[]
  ): string;
}

/**
 * Creates a Kotlin bridge generator with injected dependencies
 */
export function createKotlinBridgeGenerator(
  deps: KotlinBridgeGeneratorDeps
): KotlinBridgeGenerator {
  const { codeEmitter, methodGenerator } = deps;

  /**
   * Generates BrickModuleBase.kt interface
   */
  function generateBrickModuleBase(): string {
    const content = `${codeEmitter.generateFileHeader()}

package com.brickmodule.codegen

/**
 * Base interface that all Brick modules must implement
 * Contains only essential properties for module identification
 */
interface BrickModuleBase {
    /// The name of the module (required for registration)
    val moduleName: String

}

/**
 * Error types for Brick modules
 */
open class BrickModuleError(message: String, val errorCode: String) : Exception(message) {
    class TypeMismatch(message: String) : BrickModuleError("Type mismatch: $message", "TYPE_ERROR")
    class ExecutionError(message: String) : BrickModuleError("Execution error: $message", "EXECUTION_ERROR")
    class InvalidDefinition(message: String) : BrickModuleError("Invalid definition: $message", "DEFINITION_ERROR")
    class MethodNotFound(message: String) : BrickModuleError("Method not found: $message", "METHOD_NOT_FOUND")
    class ModuleNotFound(message: String) : BrickModuleError("Module not found: $message", "MODULE_NOT_FOUND")
}
`;
    return content;
  }

  /**
   * Generates event emitter routing method for BrickModule
   */
  function generateEventEmitterMethod(
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string {
    // Group events by module
    const eventsByModule = new Map<string, Array<ModuleEvent>>();
    for (const event of allEvents) {
      const events = eventsByModule.get(event.moduleName) || [];
      events.push(event);
      eventsByModule.set(event.moduleName, events);
    }

    // Generate when branches for each module's events
    const eventCases: string[] = [];
    for (const [moduleName, events] of eventsByModule) {
      for (const event of events) {
        const cap = event.name.charAt(0).toUpperCase() + event.name.slice(1);
        const eventKey = `on${cap}`;
        const emitMethodName = `emit${moduleName}_${eventKey}`;
        eventCases.push(
          `            "${moduleName}" to "${eventKey}" -> ${emitMethodName}(writablePayload)`
        );
      }
    }

    return `
    /**
     * Routes event emissions from modules to auto-generated event emitter methods
     * @param moduleName The name of the module emitting the event
     * @param eventName The name of the event (e.g., "onCalculationCompleted")
     * @param payload The event data as a Map
     */
    fun emitEventForModule(moduleName: String, eventName: String, payload: Map<String, Any>) {
        // Convert Map to WritableMap using Arguments
        val writablePayload = convertMapToWritableMap(payload)

        // Route to the appropriate auto-generated emit method
        when (moduleName to eventName) {
${eventCases.join("\n")}
            else -> {
                // Event not registered - log warning
                android.util.Log.w("BrickModule", "Unknown event: $moduleName.$eventName")
            }
        }
    }
`;
  }

  /**
   * Generates the main Android bridge file (BrickModule.kt)
   */
  function generateAndroidBridge(
    allMethods: Array<ModuleMethod & { moduleName: string }>,
    allConstants: Array<ModuleConstant & { moduleName: string }>,
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string {
    // Generate method implementations
    const methodImplementations = allMethods
      .map((method) => {
        // Create a minimal ParsedModule structure for the method generator
        const module: ParsedModule = {
          name: method.moduleName,
          moduleName: method.moduleName,
          version: "",
          specPath: "",
          methods: [],
          constants: [],
          events: [],
          interfaceDefinitions: [],
          sourceModule: {
            name: method.moduleName,
            path: "",
            specPath: "",
            packageJsonPath: "",
            version: "",
            hasIosImplementation: false,
            hasAndroidImplementation: false,
            brickConfig: undefined,
          },
        };
        return methodGenerator.generateKotlinMethod(method, module);
      })
      .join("\n\n");

    // Architecture-specific imports and class declaration
    const imports = `import com.facebook.fbreact.specs.NativeBrickModuleSpec
import com.brickmodule.codegen.BrickModuleImpl
import com.brickmodule.BrickModuleRegistry
import com.facebook.react.bridge.Callback
import com.facebook.react.bridge.Promise
import com.facebook.react.bridge.ReactContext
import com.facebook.react.bridge.ReactApplicationContext
import com.facebook.react.bridge.Arguments
import com.facebook.react.bridge.ReadableArray
import com.facebook.react.bridge.ReadableMap
import com.facebook.react.bridge.ReadableType
import com.facebook.react.bridge.WritableArray
import com.facebook.react.bridge.WritableMap
import com.facebook.react.bridge.WritableNativeArray
import com.facebook.react.bridge.WritableNativeMap
`;

    const classComment = `/**
 * Generated BrickModule for React Native
 * Each module method is implemented as override function
 */`;

    const classDeclaration = `class BrickModule(private val reactContext: ReactContext) :
        NativeBrickModuleSpec(reactContext as ReactApplicationContext) {`;

    // Generate event emitter routing method
    const eventEmitterMethod =
      allEvents.length > 0 ? generateEventEmitterMethod(allEvents) : "";

    const bridgeContent = `${codeEmitter.generateFileHeader()}

package com.brickmodule.codegen

${imports}

${classComment}
${classDeclaration}

    private val brickModuleImpl = BrickModuleImpl(reactContext, this)

    companion object {
        const val NAME = "BrickModule"
    }

    init {
        // Initialize implementation
    }

    override fun getName(): String {
        return NAME
    }

    ${
      allConstants.length > 0
        ? `override fun getTypedExportedConstants(): Map<String, Any?> {
        val constants: MutableMap<String, Any?> = HashMap()
${generateNewArchConstants(allConstants)}
        return constants
    }`
        : ""
    }

    override fun getRegisteredModules(): WritableArray {
        return brickModuleImpl.getRegisteredModules()
    }

${eventEmitterMethod}

    // ========== HELPER FUNCTIONS ==========

    /**
     * Converts Any type to WritableMap dynamically based on runtime type
     */
    private fun convertAnyToMap(value: Any?, map: WritableMap) {
        when (value) {
            null -> map.putNull("value")
            is String -> map.putString("value", value)
            is Boolean -> map.putBoolean("value", value)
            is Int -> map.putInt("value", value)
            is Double -> map.putDouble("value", value)
            is Float -> map.putDouble("value", value.toDouble())
            is Long -> map.putDouble("value", value.toDouble())
            is WritableMap -> map.putMap("value", value)
            is WritableArray -> map.putArray("value", value)
            is ReadableMap -> map.putMap("value", value as WritableMap)
            is ReadableArray -> map.putArray("value", value as WritableArray)
            is Map<*, *> -> {
                map.putMap("value", convertMapToWritableMap(value))
            }
            is List<*> -> {
                map.putArray("value", convertListToWritableArray(value))
            }
            else -> map.putString("value", value.toString())
        }
    }

    /**
     * Recursively converts Map to WritableMap
     */
    private fun convertMapToWritableMap(value: Map<*, *>): WritableMap {
        val writable = Arguments.createMap()
        value.forEach { (k, v) ->
            val key = k.toString()
            when (v) {
                null -> writable.putNull(key)
                is String -> writable.putString(key, v)
                is Boolean -> writable.putBoolean(key, v)
                is Int -> writable.putInt(key, v)
                is Double -> writable.putDouble(key, v)
                is Float -> writable.putDouble(key, v.toDouble())
                is Long -> writable.putDouble(key, v.toDouble())
                is Map<*, *> -> writable.putMap(key, convertMapToWritableMap(v))
                is List<*> -> writable.putArray(key, convertListToWritableArray(v))
                else -> writable.putString(key, v.toString())
            }
        }
        return writable
    }

    /**
     * Recursively converts List to WritableArray
     */
    private fun convertListToWritableArray(value: List<*>): WritableArray {
        val writable = Arguments.createArray()
        value.forEach { item ->
            when (item) {
                null -> writable.pushNull()
                is String -> writable.pushString(item)
                is Boolean -> writable.pushBoolean(item)
                is Int -> writable.pushInt(item)
                is Double -> writable.pushDouble(item)
                is Float -> writable.pushDouble(item.toDouble())
                is Long -> writable.pushDouble(item.toDouble())
                is Map<*, *> -> writable.pushMap(convertMapToWritableMap(item))
                is List<*> -> writable.pushArray(convertListToWritableArray(item))
                else -> writable.pushString(item.toString())
            }
        }
        return writable
    }

    // ========== GENERATED METHODS ==========
${methodImplementations}

    // No DeviceEventEmitter path; events handled via CodegenTypes.EventEmitter
}
`;

    return bridgeContent;
  }

  /**
   * Generates setup code for all module event emitters
   */
  function generateEventEmitterSetup(
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string {
    if (allEvents.length === 0) {
      return "";
    }

    // Get unique module names that have events
    const modulesWithEvents = Array.from(
      new Set(allEvents.map((e) => e.moduleName))
    );

    const setupCode = modulesWithEvents
      .map(
        (moduleName) => `
        // Setup event emitters for ${moduleName}
        registry.getModuleInstance("${moduleName}")?.let { module ->
            if (module is com.brickmodule.BrickModuleSpec) {
                module.eventEmitterCallback = { eventName, payload ->
                    brickModule.emitEventForModule("${moduleName}", eventName, payload)
                }
            }
        }`
      )
      .join("\n");

    return `
    /**
     * Sets up event emitter callbacks for all modules
     * Called once during initialization to register event handlers
     */
    private fun setupAllModuleEventEmitters() {
        val activity = reactContext.currentActivity as? BrickModuleRegistrar ?: return
        val registry = activity.getModuleRegistry()
${setupCode}
    }`;
  }

  /**
   * Generates Kotlin implementation file (BrickModuleImpl.kt)
   */
  function generateKotlinImplementation(
    allMethods: Array<ModuleMethod & { moduleName: string }>,
    allConstants: Array<ModuleConstant & { moduleName: string }>,
    allEvents: Array<ModuleEvent & { moduleName: string }>,
    libraryImports: string
  ): string {
    // Generate method implementations
    const methodImplementations = allMethods
      .map((method) => {
        // Create a minimal ParsedModule structure for the method generator
        const module: ParsedModule = {
          name: method.moduleName,
          moduleName: method.moduleName,
          version: "",
          specPath: "",
          methods: [],
          constants: [],
          events: [],
          interfaceDefinitions: [],
          sourceModule: {
            name: method.moduleName,
            path: "",
            specPath: "",
            packageJsonPath: "",
            version: "",
            hasIosImplementation: false,
            hasAndroidImplementation: false,
            brickConfig: undefined,
          },
        };
        return methodGenerator.generateKotlinImplMethod(method, module);
      })
      .join("\n\n");

    // Generate constant getters
    const constantGetters = allConstants
      .map((constant) => methodGenerator.generateConstantImplGetter(constant))
      .join("\n\n");

    // Generate comprehensive conversion helpers
    const conversionHelpers = generateConversionHelpers();

    // Architecture-specific imports and comments for implementation
    const implImports = `import com.facebook.react.bridge.ReactContext
import com.facebook.react.bridge.Promise
import com.facebook.react.bridge.Callback
import com.facebook.react.bridge.ReadableArray
import com.facebook.react.bridge.ReadableMap
import com.facebook.react.bridge.WritableArray
import com.facebook.react.bridge.WritableMap
import com.facebook.react.bridge.WritableNativeArray
import com.facebook.react.bridge.WritableNativeMap
import com.facebook.react.bridge.Arguments
import com.brickmodule.BrickModuleRegistry
import com.brickmodule.BrickModuleRegistrar
import com.google.gson.Gson
import com.google.gson.reflect.TypeToken
import java.util.HashMap
import java.util.ArrayList
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
${libraryImports}`;

    const implContent = `${codeEmitter.generateFileHeader()}

package com.brickmodule.codegen

${implImports}

class BrickModuleImpl(private val reactContext: ReactContext, private val brickModule: BrickModule) {
    private val gson = Gson()
    private val scope = CoroutineScope(Dispatchers.Main + SupervisorJob())

    init {
        // Setup event emitters for all modules during initialization
        setupAllModuleEventEmitters()
    }

${generateEventEmitterSetup(allEvents)}

    // ========== GENERATED METHOD IMPLEMENTATIONS ==========
${methodImplementations}

    // ========== CONSTANT GETTERS ==========
${constantGetters}

    // ========== UTILITY METHODS ==========

    fun getRegisteredModules(): WritableArray {
        val activity = reactContext.currentActivity
            ?: throw BrickModuleError.ModuleNotFound("No current activity")

        if (activity !is BrickModuleRegistrar) {
            throw BrickModuleError.ModuleNotFound("Current activity does not implement BrickModuleRegistrar")
        }

        val moduleNames = activity.getModuleRegistry().getRegisteredModules()
        val writableArray = Arguments.createArray()
        moduleNames.forEach { moduleName: String ->
            writableArray.pushString(moduleName)
        }
        return writableArray
    }

${conversionHelpers}
}
`;

    return implContent;
  }

  /**
   * Generates constants for New Architecture (getTypedExportedConstants)
   */
  function generateNewArchConstants(
    constants: Array<ModuleConstant & { moduleName: string }>
  ): string {
    if (constants.length === 0) {
      return "";
    }

    return constants
      .map((constant) => {
        const constantKey = `${constant.moduleName}_${constant.name}`;
        return `        constants["${constantKey}"] = brickModuleImpl.${constant.moduleName}_${constant.name}()`;
      })
      .join("\n");
  }

  /**
   * Generates library imports for data types used in BrickModuleImpl
   */
  function generateLibraryImports(modules: ParsedModule[]): string {
    const imports = new Set<string>();

    for (const module of modules) {
      // Check if this is a local module (no brickConfig) or library module
      const isLocalModule = !module.sourceModule.brickConfig;

      // Get Android package based on module type
      const androidPackage = isLocalModule
        ? "com.brickmodule.codegen"
        : module.sourceModule.brickConfig?.android?.package ||
          "com.brickmodule";

      // Import the Spec interface
      imports.add(`import ${androidPackage}.${module.moduleName}Spec`);

      // Import all interface definitions (data classes)
      for (const interfaceDef of module.interfaceDefinitions || []) {
        imports.add(`import ${androidPackage}.${interfaceDef.name}`);
      }
    }

    return Array.from(imports).join("\n");
  }

  /**
   * Generates conversion helper methods
   */
  function generateConversionHelpers(): string {
    return `
    // ========== CONVERSION HELPERS ==========

    private fun <T> convertFromReadableMap(readableMap: ReadableMap, type: Class<T>): T {
        // Convert ReadableMap to a proper HashMap to avoid LinkedHashMap issues
        val map = convertReadableMapToHashMap(readableMap)
        val jsonString = gson.toJson(map)
        return gson.fromJson(jsonString, type)
    }

    private fun convertToWritableMap(obj: Any): WritableMap {
        val jsonString = gson.toJson(obj)
        val map = gson.fromJson(jsonString, object : TypeToken<Map<String, Any>>() {}.type) as Map<String, Any>
        return Arguments.makeNativeMap(map)
    }

    private fun convertToWritableArray(obj: Any): WritableArray {
        val jsonString = gson.toJson(obj)
        val list = gson.fromJson(jsonString, object : TypeToken<List<Any>>() {}.type) as List<Any>
        val writableArray = Arguments.createArray()
        list.forEach { item ->
            when (item) {
                is String -> writableArray.pushString(item)
                is Double -> writableArray.pushDouble(item)
                is Boolean -> writableArray.pushBoolean(item)
                is Int -> writableArray.pushInt(item)
                is Map<*, *> -> writableArray.pushMap(Arguments.makeNativeMap(item as Map<String, Any>))
                is List<*> -> writableArray.pushArray(Arguments.makeNativeArray(item as ArrayList<Any>))
                else -> writableArray.pushString(item.toString())
            }
        }
        return writableArray
    }

    /**
     * Converts Any type value to appropriate React Native type dynamically
     * Used for Promise<Any> return types
     */
    private fun convertAnyValue(value: Any?): Any? {
        return when (value) {
            null -> null
            is String, is Boolean, is Int, is Double, is Float, is Long -> value
            is WritableMap, is WritableArray, is ReadableMap, is ReadableArray -> value
            is Map<*, *> -> convertToWritableMap(value)
            is List<*> -> convertToWritableArray(value)
            else -> value
        }
    }

    private fun <T> convertFromReadableArray(readableArray: ReadableArray, elementType: Class<T>): List<T> {
        val list = convertReadableArrayToArrayList(readableArray)
        val jsonString = gson.toJson(list)
        val listType = TypeToken.getParameterized(java.util.List::class.java, elementType).type
        return gson.fromJson(jsonString, listType)
    }

    private fun convertStringArrayFromReadableArray(readableArray: ReadableArray): List<String> {
        val list = ArrayList<String>()
        for (i in 0..(readableArray.size() - 1)) {
            val str = readableArray.getString(i)
            if (str != null) {
                list.add(str)
            }
        }
        return list
    }

    private fun convertDoubleArrayFromReadableArray(readableArray: ReadableArray): List<Double> {
        val list = ArrayList<Double>()
        for (i in 0..(readableArray.size() - 1)) {
            list.add(readableArray.getDouble(i))
        }
        return list
    }

    private fun convertReadableMapToHashMap(readableMap: ReadableMap?): HashMap<String, Any?>? {
        if (readableMap == null) return null

        val map = HashMap<String, Any?>()
        val keyIterator = readableMap.keySetIterator()
        while (keyIterator.hasNextKey()) {
            val key = keyIterator.nextKey()
            val value = when (readableMap.getType(key)) {
                com.facebook.react.bridge.ReadableType.Null -> null
                com.facebook.react.bridge.ReadableType.Boolean -> readableMap.getBoolean(key)
                com.facebook.react.bridge.ReadableType.Number -> readableMap.getDouble(key)
                com.facebook.react.bridge.ReadableType.String -> readableMap.getString(key)
                com.facebook.react.bridge.ReadableType.Map -> convertReadableMapToHashMap(readableMap.getMap(key))
                com.facebook.react.bridge.ReadableType.Array -> convertReadableArrayToArrayList(readableMap.getArray(key))
            }
            map.put(key, value)
        }
        return map
    }

    private fun convertReadableArrayToArrayList(readableArray: ReadableArray?): ArrayList<Any?>? {
        if (readableArray == null) return null

        val list = ArrayList<Any?>()
        for (i in 0..(readableArray.size() - 1)) {
            val value = when (readableArray.getType(i)) {
                com.facebook.react.bridge.ReadableType.Null -> null
                com.facebook.react.bridge.ReadableType.Boolean -> readableArray.getBoolean(i)
                com.facebook.react.bridge.ReadableType.Number -> readableArray.getDouble(i)
                com.facebook.react.bridge.ReadableType.String -> readableArray.getString(i)
                com.facebook.react.bridge.ReadableType.Map -> convertReadableMapToHashMap(readableArray.getMap(i))
                com.facebook.react.bridge.ReadableType.Array -> convertReadableArrayToArrayList(readableArray.getArray(i))
            }
            list.add(value)
        }
        return list
    }`;
  }

  /**
   * Generate build.gradle
   */
  function generateBuildGradle(
    brickModules: { npmName: string; gradleProjectName: string }[]
  ): string {
    // Generate dynamic dependency declarations
    const brickModuleDependencies = brickModules
      .map(
        (
          module
        ) => `    if (project.rootProject.findProject(':${module.gradleProjectName}')) {
        implementation project(':${module.gradleProjectName}')
    }`
      )
      .join("\n");

    const content = `${codeEmitter.generateFileHeader()}

apply plugin: 'com.android.library'
apply plugin: 'kotlin-android'

android {
    namespace "com.brickmodule.generated"
    compileSdkVersion 33

    defaultConfig {
        minSdkVersion 21
        targetSdkVersion 33
        versionCode 1
        versionName "1.0"
    }

    sourceSets {
        main {
            java {
                srcDirs = ['src/main/java']
            }
            kotlin {
                srcDirs = ['src/main/kotlin']
            }
        }
    }

    compileOptions {
        sourceCompatibility JavaVersion.VERSION_17
        targetCompatibility JavaVersion.VERSION_17
    }

    kotlinOptions {
        jvmTarget = '17'
    }
}

dependencies {
    api 'com.facebook.react:react-android:+'
    implementation "org.jetbrains.kotlin:kotlin-stdlib-jdk8:1.7.10"
    implementation 'org.jetbrains.kotlinx:kotlinx-coroutines-android:1.6.4'
    implementation 'com.google.code.gson:gson:2.8.9'

    // Include brick-module for base interfaces
    if (project.rootProject.findProject(':brick-module')) {
        implementation project(':brick-module')
    }

    // Auto-detected brick modules
${brickModuleDependencies}
}
`;

    return content;
  }

  return {
    generateBrickModuleBase,
    generateAndroidBridge,
    generateKotlinImplementation,
    generateLibraryImports,
    generateBuildGradle,
  };
}
